import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  Plus, 
  Image, 
  Video, 
  Package, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Layers
} from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentShoots, setRecentShoots] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, shootsRes, subRes] = await Promise.all([
        axios.get(`${API}/usage/stats`, { withCredentials: true }),
        axios.get(`${API}/shoots`, { withCredentials: true }),
        axios.get(`${API}/user/subscription`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setRecentShoots(shootsRes.data.slice(0, 5));
      setSubscription(subRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "text-green-600 bg-green-50";
      case "processing": return "text-blue-600 bg-blue-50";
      case "queued": return "text-yellow-600 bg-yellow-50";
      case "failed": return "text-red-600 bg-red-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-4 h-4" />;
      case "processing": return <Clock className="w-4 h-4 animate-spin" />;
      case "queued": return <Clock className="w-4 h-4" />;
      case "failed": return <AlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const skuUsagePercent = stats?.sku_limit ? (stats.skus_used / stats.sku_limit) * 100 : 0;

  return (
    <DashboardLayout>
      <div data-testid="customer-dashboard" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
              Welcome back, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-neutral-600 mt-1">
              {subscription?.plan?.name || "Free"} Plan • {stats?.sku_limit - stats?.skus_used || 0} SKUs remaining
            </p>
          </div>
          <Link to="/new-shoot">
            <Button data-testid="new-shoot-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-full px-6">
              <Plus className="w-4 h-4 mr-2" />
              New AI Shoot
            </Button>
          </Link>
        </div>

        {/* Usage Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-indigo-600" />
                </div>
                <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">SKUs</span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{stats?.skus_used || 0}</p>
              <p className="text-sm text-neutral-500 mt-1">of {stats?.sku_limit || 0} used</p>
              <Progress value={skuUsagePercent} className="mt-3 h-2" />
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                  <Image className="w-5 h-5 text-violet-600" />
                </div>
                <span className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-1 rounded-full">Images</span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{stats?.images_generated || 0}</p>
              <p className="text-sm text-neutral-500 mt-1">Images generated</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Video className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Videos</span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{stats?.videos_generated || 0}</p>
              <p className="text-sm text-neutral-500 mt-1">Videos generated</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Plan</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{subscription?.plan?.name || "Free Trial"}</p>
              <Link to="/usage" className="text-sm text-indigo-600 hover:text-indigo-700 mt-1 inline-flex items-center gap-1">
                Upgrade <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Recent Shoots */}
        <Card className="border-neutral-100">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="font-display text-xl">Recent Shoots</CardTitle>
            <Link to="/shoots" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              View All
            </Link>
          </CardHeader>
          <CardContent>
            {recentShoots.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-neutral-400" />
                </div>
                <p className="text-neutral-600 mb-4">No shoots yet. Create your first AI shoot!</p>
                <Link to="/new-shoot">
                  <Button className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Create AI Shoot
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentShoots.map((shoot) => (
                  <Link 
                    key={shoot.shoot_id} 
                    to={`/shoots/${shoot.shoot_id}`}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-neutral-50 transition-colors group"
                  >
                    <img 
                      src={shoot.sku?.uploaded_image_url || "https://placehold.co/80x80/e5e5e5/a3a3a3?text=SKU"}
                      alt={shoot.sku?.sku_name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 truncate">{shoot.sku?.sku_name || "Unnamed SKU"}</p>
                      <p className="text-sm text-neutral-500">
                        {shoot.model?.name} • {shoot.background?.name}
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(shoot.status)}`}>
                      {getStatusIcon(shoot.status)}
                      <span className="capitalize">{shoot.status}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-indigo-600 transition-colors" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Link to="/new-shoot" className="block">
            <Card className="border-neutral-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <Plus className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">New AI Shoot</p>
                  <p className="text-sm text-neutral-500">Single product</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/bulk-upload" className="block">
            <Card className="border-neutral-100 hover:border-violet-200 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                  <Layers className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">Bulk Upload</p>
                  <p className="text-sm text-neutral-500">Multiple products</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/downloads" className="block">
            <Card className="border-neutral-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <Image className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">Downloads</p>
                  <p className="text-sm text-neutral-500">Access assets</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/usage" className="block">
            <Card className="border-neutral-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">Usage & Billing</p>
                  <p className="text-sm text-neutral-500">Manage plan</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
